import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useCityNavigate } from '../../components/nav';
import { loadChapter } from '../../data/load';
import type { Chapter, Mention, Paragraph } from '../../data/types';
import { useAppData } from '../../state/AppData';
import { useSelection } from '../../state/selection';
import { Facsimile } from './Facsimile';
import { paginaEnLinea } from './page';
import { splitParagraph } from './segments';

export function Reader({ chapterId, view: vistaExterna, onView }: {
  chapterId: string;
  /** en el móvil la vista la manda la barra única de pestañas; en escritorio la lleva el lector */
  view?: 'text' | 'facsimile';
  onView?: (v: 'text' | 'facsimile') => void;
}) {
  const [chapter, setChapter] = useState<Chapter | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vistaPropia, setVistaPropia] = useState<'text' | 'facsimile'>('text');
  const view = vistaExterna ?? vistaPropia;
  const setView = (v: 'text' | 'facsimile') => { setVistaPropia(v); onView?.(v); };
  const [currentPage, setCurrentPage] = useState<number | undefined>();
  const [facPage, setFacPage] = useState<number | undefined>();
  const { index, city } = useAppData();
  const sel = useSelection();
  const containerRef = useRef<HTMLDivElement>(null);
  // el seguimiento de lectura vive en un efecto que solo se rehace al cambiar de capítulo; sin esta
  // referencia escribiría en la URL con una versión vieja de la selección y borraría el lugar y la
  // mención elegidos un segundo antes (se veía en móvil: la ficha se quedaba en blanco sola)
  const selRef = useRef(sel);
  selRef.current = sel;
  const navigate = useCityNavigate();

  useEffect(() => {
    let alive = true;
    setChapter(null);
    loadChapter(chapterId).then((c) => alive && setChapter(c)).catch((e: Error) => alive && setError(e.message));
    return () => { alive = false; };
  }, [chapterId]);

  const { body, notesByHost } = useMemo(() => {
    const notes = new Map<string, Paragraph[]>();
    const main: Paragraph[] = [];
    for (const p of chapter?.paragraphs ?? []) {
      if (p.kind === 'footnote') {
        const host = p.id.replace(/-n\d+$/, '');
        notes.set(host, [...(notes.get(host) ?? []), p]);
      } else main.push(p);
    }
    return { body: main, notesByHost: notes };
  }, [chapter]);

  // Desplazamiento solicitado desde el mapa, la ficha o la URL (se consume una vez).
  useEffect(() => {
    if (!chapter || !sel.jump) return;
    const target = document.getElementById(sel.jump.paragraphId);
    if (!target) return;
    const details = target.closest('details');
    if (details) details.open = true;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    // se desplaza solo el panel de lectura (scrollIntoView movería también los contenedores externos)
    const box = containerRef.current;
    if (box) {
      const top = target.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - box.clientHeight / 3;
      box.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
    }
    const focusEl = (sel.jump.mentionId && document.querySelector<HTMLElement>(`[data-mention="${sel.jump.mentionId}"]`)) || target;
    focusEl.focus({ preventScroll: true });
    sel.consumeJump();
  }, [chapter, sel.jump, sel]);

  // Seguimiento pasivo del párrafo dominante: actualiza la página visible y, con retardo, la URL.
  useEffect(() => {
    const root = containerRef.current;
    if (!chapter || !root || typeof IntersectionObserver === 'undefined') return;
    let timer: number | undefined;
    const visible = new Set<HTMLElement>();
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) visible.add(e.target as HTMLElement);
        else visible.delete(e.target as HTMLElement);
      }
      // el callback solo recibe los cambios: se elige el más alto entre todos los visibles
      // las láminas sin numerar no cuentan para la página actual
      const el = [...visible].filter((v) => v.dataset.page).sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)[0];
      if (!el) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => selRef.current.setReadingParagraph(el.id), 800);
    }, { root, rootMargin: '0px 0px -60% 0px', threshold: 0 });
    root.querySelectorAll('[data-paragraph]').forEach((el) => obs.observe(el));
    return () => { obs.disconnect(); window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter]);

  // La página que se está leyendo sale de los anclajes: el comienzo de cada párrafo y los saltos
  // marcados dentro de ellos. Antes se tomaba la página en que empieza el párrafo visible, y como
  // tres de cada cuatro párrafos cruzan un salto, el facsímil se quedaba una página atrás.
  useEffect(() => {
    const root = containerRef.current;
    if (!chapter || !root) return;
    let pendiente = 0;
    const medir = () => {
      const caja = root.getBoundingClientRect();
      const linea = caja.top + Math.min(caja.height * 0.3, 220);
      const anclas = [...root.querySelectorAll<HTMLElement>('[data-page-anchor]')]
        .map((el) => ({ top: el.getBoundingClientRect().top, page: Number(el.dataset.pageAnchor) }))
        .filter((a) => !Number.isNaN(a.page));
      const page = paginaEnLinea(anclas, linea);
      if (page !== undefined) setCurrentPage(page);
    };
    const alDesplazar = () => {
      if (pendiente) return;
      pendiente = requestAnimationFrame(() => { pendiente = 0; medir(); });
    };
    medir();
    root.addEventListener('scroll', alDesplazar, { passive: true });
    return () => { root.removeEventListener('scroll', alDesplazar); if (pendiente) cancelAnimationFrame(pendiente); };
  }, [chapter, view]);

  // al volver del facsímil, el texto se sitúa en la página que se estaba viendo
  const showText = () => {
    setView('text');
    const page = facPage;
    if (page === undefined || page === currentPage) return;
    requestAnimationFrame(() => {
      const box = containerRef.current;
      if (!box) return;
      const els = [...box.querySelectorAll<HTMLElement>('[data-page-anchor]')];
      const target = els.filter((el) => Number(el.dataset.pageAnchor) <= page && !Number.isNaN(Number(el.dataset.pageAnchor))).pop();
      if (target) {
        box.scrollTop = target.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - 16;
        // el anclaje puede ser un salto dentro de un párrafo: el foco va al párrafo que lo contiene
        const parrafo = target.closest<HTMLElement>('[data-paragraph]') ?? target;
        parrafo.focus({ preventScroll: true });
      }
    });
  };

  const meta = index.chapters.find((c) => c.chapterId === chapterId);
  const pos = index.chapters.findIndex((c) => c.chapterId === chapterId);
  const prev = index.chapters[pos - 1];
  const next = index.chapters[pos + 1];

  if (error) return <div role="alert" className="reader__error">{error}</div>;
  if (!chapter || !meta) return <div className="reader__loading" role="status">Cargando capítulo…</div>;

  let lastPage: number | undefined;
  // el aviso de transcripción va tras el primer párrafo, no antes de él
  const primerCuerpo = body.findIndex((x) => x.kind === 'body');
  return (
    <section className="reader" aria-labelledby="reader-title">
      <div className="reader__toolbar">
        <div className="segmented reader__views" role="group" aria-label="Modo de lectura">
          <button aria-pressed={view === 'text'} onClick={() => showText()}>Texto</button>
          <button aria-pressed={view === 'facsimile'} onClick={() => { setFacPage(undefined); setView('facsimile'); }}>Página original</button>
        </div>
        <label className="reader__chapter-select">
          <span className="visually-hidden">Capítulo</span>
          <select value={chapterId} onChange={(e) => navigate(`/leer/${e.target.value}`)}>
            {index.chapters.map((c) => <option key={c.chapterId} value={c.chapterId}>Cap. {c.number}</option>)}
          </select>
        </label>
        <span className="reader__page" aria-live="off">p. {(view === 'facsimile' ? facPage : currentPage) ?? meta.printedPageStart}</span>
      </div>
      {view === 'facsimile' && (
        <div className="reader__facsimile">
          <Facsimile chapterId={chapterId} printedPage={currentPage ?? meta.printedPageStart} onPageChange={setFacPage} />
        </div>
      )}
      <div ref={containerRef} id="texto" className="reader__scroll" tabIndex={-1} hidden={view !== 'text'}>
        <header className="reader__head">
          <p className="reader__eyebrow">{meta.part} · pp. {meta.printedPageStart}–{meta.printedPageEnd}</p>
          <h1 id="reader-title">Capítulo {meta.number}. {meta.title}</h1>

        </header>
        {body.map((p, i) => {
          if (p.kind === 'heading') return null;
          // el aviso de transcripción iba antes del primer párrafo: ocupaba el sitio donde el lector
          // busca por dónde empezar para decirle algo que solo importa cuando ya está leyendo
          const avisoOcr = i === primerCuerpo ? (
            <details key="ocr" className="reader__ocr">
              <summary>Transcripción en revisión</summary>
              <p>Texto procedente del OCR de la edición de 1884, con las correcciones registradas. La página
                original está siempre a un clic. <Link to="/proyecto/fuentes">Fuentes y método</Link></p>
            </details>
          ) : null;
          const showPage = p.printedPageStart !== undefined && p.printedPageStart !== lastPage;
          const pageMarker = showPage ? <span className="page-mark" aria-label={`Página ${p.printedPageStart}`}>[p. {p.printedPageStart}]</span> : null;
          if (p.printedPageStart !== undefined) lastPage = p.printedPageEnd ?? p.printedPageStart;
          if (p.kind === 'caption') {
            return (
              <p key={p.id} id={p.id} className="plate-caption" data-paragraph data-page={p.printedPageStart} data-page-anchor={p.printedPageStart} tabIndex={-1}>
                <span className="plate-caption__label">Lámina</span> {p.text}
              </p>
            );
          }
          const notes = notesByHost.get(p.id) ?? [];
          return (
            <Fragment key={p.id}>
              <p id={p.id} className="para" data-paragraph data-page={p.printedPageStart} data-page-anchor={p.printedPageStart} tabIndex={-1}>
                {pageMarker}
                <ParagraphText p={p} />
                <CopyLink id={p.id} chapterId={chapterId} />
              </p>
              {avisoOcr}
              {notes.length > 0 && (
                <details className="notes">
                  <summary>Notas de Quadrado ({notes.length})</summary>
                  {notes.map((n) => (
                    <p key={n.id} id={n.id} className="note" data-page={n.printedPageStart} tabIndex={-1}>
                      <ParagraphText p={n} />
                    </p>
                  ))}
                </details>
              )}
            </Fragment>
          );
        })}
        <nav className="reader__pager" aria-label="Continuar leyendo">
          {prev ? <Link to={`/leer/${prev.chapterId}`}>← Capítulo {prev.number}</Link> : <span />}
          {next ? <Link to={`/leer/${next.chapterId}`}>Continuar leyendo: capítulo {next.number} →</Link> : <span>Fin de la sección de {city.section}</span>}
        </nav>
      </div>
    </section>
  );
}

function ParagraphText({ p }: { p: Paragraph }) {
  const { mentionsByParagraph, placeById } = useAppData();
  const sel = useSelection();
  const mentions: Mention[] = mentionsByParagraph.get(p.id) ?? [];
  const segs = useMemo(
    // cada salto lleva su propia página: con un solo salto etiquetado con printedPageEnd, un
    // párrafo que cruzaba de la 549 a la 551 marcaba «551» donde empezaba en realidad la 550
    () => splitParagraph(p.text, mentions, p.pageBreaks
      ? p.pageBreaks.map((b) => ({ offset: b.offset, page: b.printedPage ?? undefined }))
      : p.pageBreakOffset !== undefined
        ? [{ offset: p.pageBreakOffset, page: p.printedPageEnd }]
        : undefined),
    [p, mentions],
  );
  return (
    <>
      {segs.map((s, i) => {
        if (s.kind === 'text') return <Fragment key={i}>{s.text}</Fragment>;
        if (s.kind === 'pagebreak') return <span key={i} className="page-mark page-mark--inline" data-page-anchor={s.page} aria-label={`Página ${s.page}`}>[p. {s.page}]</span>;
        const place = placeById.get(s.mention.placeId)!;
        const active = sel.place === s.mention.placeId;
        const current = sel.mention === s.mention.id;
        return (
          <button key={i} type="button" className={`mention${active ? ' is-active' : ''}${current ? ' is-current' : ''}`}
            data-mention={s.mention.id} data-place={s.mention.placeId} aria-pressed={active}
            aria-describedby={undefined}
            title={`${place.properties.canonicalName} · ver en el mapa`}
            onClick={() => sel.selectMention(s.mention, 'text')}>
            {s.text}
            <span className="visually-hidden"> (lugar: {place.properties.canonicalName})</span>
          </button>
        );
      })}
    </>
  );
}

function CopyLink({ id, chapterId }: { id: string; chapterId: string }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    const url = `${location.origin}/leer/${chapterId}?paragraph=${id}`;
    try { await navigator.clipboard.writeText(url); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* sin portapapeles */ }
  };
  return (
    <button type="button" className="para__link" onClick={copy} aria-label={`Copiar enlace al párrafo ${id}`}>
      {done ? '✓' : '¶'}
    </button>
  );
}
